import { inject, Injectable } from "@angular/core";
import { map, Observable, catchError, throwError, tap } from "rxjs";
import { HttpClient } from "@angular/common/http";
import { GetIcsEvaluationGQL, GetIcsEvaluationQuery, IcsEvaluationQuantitativeScoresInput, IcsEvaluationUpdate, UpdateIcsEvaluationGaltConfirmationStatusGQL, UpdateIcsEvaluationGaltConfirmationStatusMutation, UpdateIcsEvaluationGQL, UpdateIcsEvaluationLocalChiefConfirmationStatusGQL, UpdateIcsEvaluationLocalChiefConfirmationStatusMutation, UpdateIcsEvaluationMutation, UpdateIcsEvaluationQuantitativeScoresGQL, UpdateIcsEvaluationQuantitativeScoresMutation } from "../../../graphql/generated";
import { convertLazyLoadToComplexWithoutPage } from "./campaign-dashboard.service";
import { environment } from "../../environments/environment";
import { Apollo } from "apollo-angular";
import { DOWNLOAD_ICS_EVALUATION } from "../../../graphql/gqls/ics-downloadGQL";


@Injectable({
    providedIn: 'root'
})
export class IcsEvaluationService {
    private readonly GetIcsEvaluationGQL: GetIcsEvaluationGQL = inject(GetIcsEvaluationGQL);
    private readonly UpdateIcsEvaluationGQL: UpdateIcsEvaluationGQL = inject(UpdateIcsEvaluationGQL);
    private readonly UpdateLocalChiefConfirmationStatusGQL: UpdateIcsEvaluationLocalChiefConfirmationStatusGQL = inject(UpdateIcsEvaluationLocalChiefConfirmationStatusGQL);
    private readonly UpdateGaltConfirmationStatusGQL: UpdateIcsEvaluationGaltConfirmationStatusGQL = inject(UpdateIcsEvaluationGaltConfirmationStatusGQL);
    private readonly UpdateIcsEvaluationQuantitativeScoresGQL: UpdateIcsEvaluationQuantitativeScoresGQL = inject(UpdateIcsEvaluationQuantitativeScoresGQL);
    private readonly http: HttpClient = inject(HttpClient);
    private readonly apollo: Apollo = inject(Apollo);


    getIcsEvaluation(icsCampaignId: string, parameters: any): Observable<GetIcsEvaluationQuery['getIcsEvaluations']> {
        const param = convertLazyLoadToComplexWithoutPage(parameters);
        return this.GetIcsEvaluationGQL.fetch({ icsCampaignId, query: param.query, sort: param.sort, page: param.page }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data.getIcsEvaluations))
    }


    updateIcsEvaluation(icsEvaluationUpdates: IcsEvaluationUpdate[]): Observable<UpdateIcsEvaluationMutation['updateIcsEvaluation']> {
        return this.UpdateIcsEvaluationGQL.mutate({ icsEvaluationUpdates }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.updateIcsEvaluation))
    }

    updateIcsEvaluationQuantitativeScores(input: IcsEvaluationQuantitativeScoresInput): Observable<UpdateIcsEvaluationQuantitativeScoresMutation['updateIcsEvaluationQuantitativeScores']> {
        return this.UpdateIcsEvaluationQuantitativeScoresGQL.mutate({ input }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.updateIcsEvaluationQuantitativeScores))
    }

    exportAuditExecution(icsCampaignId: string): Observable<{ blob: Blob, filename?: string }> {
        const url = `${environment.API_URL}ics_raw_data/engagements?campaign=${icsCampaignId}`;

        return this.http.get(url, {
            responseType: 'blob',
            observe: 'response'
        }).pipe(
            map(response => ({
                blob: response.body!,
                filename: this.extractFilenameFromHeader(response.headers.get('content-disposition'))
            })),
            catchError((error: any) => {
                return throwError(() => error);
            })
        );
    }

    exportIssueFollowUp(icsCampaignId: string): Observable<{ blob: Blob, filename?: string }> {
        return this.http.get(`${environment.API_URL}ics_raw_data/issues?campaign=${icsCampaignId}`, {
            responseType: 'blob',
            observe: 'response'
        }).pipe(
            map(response => ({
                blob: response.body!,
                filename: this.extractFilenameFromHeader(response.headers.get('content-disposition'))
            }))
        );
    }

    exportInspection(icsCampaignId: string): Observable<{ blob: Blob, filename?: string }> {
        return this.http.get(`${environment.API_URL}ics_raw_data/inspections?campaign=${icsCampaignId}`, {
            responseType: 'blob',
            observe: 'response'
        }).pipe(
            map(response => ({
                blob: response.body!,
                filename: this.extractFilenameFromHeader(response.headers.get('content-disposition'))
            }))
        );
    }

    exportSanction(icsCampaignId: string): Observable<{ blob: Blob, filename?: string }> {
        return this.http.get(`${environment.API_URL}ics_raw_data/sanctions?campaign=${icsCampaignId}`, {
            responseType: 'blob',
            observe: 'response'
        }).pipe(
            map(response => ({
                blob: response.body!,
                filename: this.extractFilenameFromHeader(response.headers.get('content-disposition'))
            }))
        );
    }

    exportComplaints(icsCampaignId: string): Observable<{ blob: Blob, filename?: string }> {
        return this.http.get(`${environment.API_URL}ics_raw_data/complaints?campaign=${icsCampaignId}`, {
            responseType: 'blob',
            observe: 'response'
        }).pipe(
            map(response => ({
                blob: response.body!,
                filename: this.extractFilenameFromHeader(response.headers.get('content-disposition'))
            }))
        );
    }

    private extractFilenameFromHeader(contentDisposition: string | null): string | undefined {
        if (!contentDisposition) return undefined;
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
            let filename = filenameMatch[1].replace(/['"]/g, '').trim();
            if (!filename.toLowerCase().endsWith('.csv')) {
                filename += '.csv';
            }
            return filename;
        }
        return undefined;
    }

    updateLocalChiefConfirmationStatus(evaluationId: string, localChiefConfirmationStatus: boolean): Observable<UpdateIcsEvaluationLocalChiefConfirmationStatusMutation['updateIcsEvaluationLocalChiefConfirmationStatus']> {
        return this.UpdateLocalChiefConfirmationStatusGQL.mutate({ evaluationId, localChiefConfirmationStatus }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.updateIcsEvaluationLocalChiefConfirmationStatus))
    }

    updateGaltConfirmationStatus(evaluationId: string, galtConfirmationStatus: boolean): Observable<UpdateIcsEvaluationGaltConfirmationStatusMutation['updateIcsEvaluationGaltConfirmationStatus']> {
        return this.UpdateGaltConfirmationStatusGQL.mutate({ evaluationId, galtConfirmationStatus }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.updateIcsEvaluationGaltConfirmationStatus))
    }

    downloadIcsEvaluation(query: any, icsCampaignId?: string, timeZone?: string): Observable<any> {
        return this.apollo.mutate({
            mutation: DOWNLOAD_ICS_EVALUATION,
            variables: {
                query: query,
                icsCampaignId: icsCampaignId,
                timeZone: timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone
            }
        }).pipe(
            map((res: any) => res.data?.downloadIcsEvaluation),
            tap((response: any) => this.handleDownload(response))
        );
    }

    private handleDownload(response: any) {
        if (response?.data && Array.isArray(response.data) && response.data.length > 0) {
            const file = response.data[0];
            if (file.filename && file.content) {
                const blob = this.base64ToBlob(file.content, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = file.filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
            }
        }
    }

    private base64ToBlob(base64: string, mimeType: string): Blob {
        const byteChars = atob(base64);
        const byteNumbers = new Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) {
            byteNumbers[i] = byteChars.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        return new Blob([byteArray], { type: mimeType });
    }
}
